import { useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { EXPANDED_ROWS_LIMIT } from 'src/constants/table';
import { useInfiniteWin32AppWdacFiles } from 'src/hooks/wdac';
import { formatCount } from 'src/utils/formatNumber.util';
import { TELEMETRY_REPORTED_WINDOW } from 'src/utils/enums/telemetryReportedWindow';
import Win32FileTable, {
  DEFAULT_FILE_SORT,
  DEFAULT_VISIBLE_WIN32_FILE_COLUMNS,
  WIN32_FILE_COLUMNS,
  toggleFileSort,
} from './Tabs/Win32FileTable';

const COLUMNS = WIN32_FILE_COLUMNS.filter(column =>
  DEFAULT_VISIBLE_WIN32_FILE_COLUMNS.includes(column.key),
);

const DEFAULT_SCOPE = { reportedWindow: TELEMETRY_REPORTED_WINDOW.Last90Days };

// The files a status save would import, read the way the server reads them for it: every reported
// file the app does not cover under the import's window and filters. Narrowing them down is the
// telemetry tab's job.
const UncoveredFilesModal = ({
  open,
  onClose,
  globalApplicationVersionId,
  scope = DEFAULT_SCOPE,
  title = 'Uncovered Files in Last 90 days',
}) => {
  const [sort, setSort] = useState(DEFAULT_FILE_SORT);

  const filesQuery = useInfiniteWin32AppWdacFiles({
    globalApplicationVersionId,
    isCovered: false,
    reportedWindow: scope.reportedWindow,
    isSigned: scope.isSigned,
    search: scope.search,
    sortColumn: sort.column,
    sortDesc: sort.direction === 'desc',
    limit: EXPANDED_ROWS_LIMIT,
    enabled: open,
  });

  const rows = filesQuery.data?.pages?.flatMap(page => page.results || []) || [];
  const totalCount = filesQuery.data?.pages?.[0]?.totalCount;

  const [sentryRef] = useInfiniteScroll({
    loading: filesQuery.isFetchingNextPage,
    hasNextPage: !!filesQuery.hasNextPage,
    onLoadMore: filesQuery.fetchNextPage,
    rootMargin: '0px 0px 400px 0px',
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#3f3f3f' } }}>
      <DialogTitle sx={{ pr: 6, pb: 0.5 }}>
        {title}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: '#CFCFCF' }}>
          <Iconify icon="mdi:close" width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: '#ccc', mb: 2 }}>
          {totalCount === undefined
            ? 'Reported by devices and not yet covered by any of the app’s file set imports.'
            : `${formatCount(totalCount)} ${
                totalCount === 1 ? 'file' : 'files'
              } reported by devices and not yet covered by any of the app’s file set imports.`}
        </Typography>
        <Win32FileTable
          rows={rows}
          columns={COLUMNS}
          isLoading={filesQuery.isLoading}
          isFetchingNextPage={filesQuery.isFetchingNextPage}
          lastElementRef={sentryRef}
          searchValue=""
          sort={sort}
          onSort={sortKey => setSort(previous => toggleFileSort(previous, sortKey))}
          emptyText="Every file reported here is already in the app."
        />
      </DialogContent>
    </Dialog>
  );
};

export default UncoveredFilesModal;
